import cn from 'classnames';
import { useRouter } from 'next/router';
import React, { memo, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { Icon, Link } from 'components';
import { Button, Form as FormComponent, Password } from 'components/form';
import { Col, Row } from 'components/layout';

import { API_ROUTES } from 'lib/api-routes';
import { Notice } from 'lib/models/notice';
import { PAGE_ROUTES } from 'lib/page-routes';
import { patch } from 'lib/utils/http';
import { parseErrorResponse } from 'lib/utils/parser';

import styles from './Form.module.scss';

interface Inputs {
  password: string;
}

export interface FormProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  /** on email submit */
  isRecruiter?: boolean;
}

const getErrorArray = (password: string) => {
  const errorList = [];
  //length
  if (password.length < 8) {
    errorList.push({ message: 'At least 8 characters', type: 'password' });
  }
  //uppercase
  if (!/^(?=.*[A-Z]).*$/.test(password)) {
    errorList.push({ message: '1 uppercase letter', type: 'password' });
  }
  //number and symbol
  if (!/^(?=.*[0-9!@#$%^&*()_\-+=]).*$/.test(password)) {
    errorList.push({ message: '1 number or symbol', type: 'password' });
  }
  return errorList;
};

export const Form: React.FC<FormProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    isRecruiter = false,
  }: FormProps) => {
    const contentClassNames = cn([styles.container], className);
    const [formError, setFormError] = React.useState<
      { message: string; type?: string }[]
    >([]);
    const router = useRouter();
    const { resetPasswordToken } = router.query;

    const onPasswordSubmit = async ({
      password,
    }: {
      password: string;
    }): Promise<void> => {
      const body = {
        password,
        reset_password_token: resetPasswordToken,
      };

      try {
        await patch<Notice>(API_ROUTES.PASSWORD_RESET, body);
        router.push(
          isRecruiter
            ? PAGE_ROUTES.SIGN_IN_RECRUITER
            : PAGE_ROUTES.SIGN_IN_SOCIETY
        );
      } catch (error) {
        const message = parseErrorResponse(error);
        setFormError([{ message }]);
        throw error;
      }
    };

    const {
      register,
      handleSubmit,
      formState: { errors, isSubmitting, isDirty },
      watch,
    } = useForm();

    const passwordValue = watch('password');

    useEffect(() => {
      if (!passwordValue && !isDirty) {
        setFormError([]);
        return;
      }
      const errorList = getErrorArray(passwordValue);
      if (errorList.length > 0) {
        setFormError(errorList);
        return;
      } else {
        setFormError([]);
      }
    }, [passwordValue]);

    const onSubmit: SubmitHandler<Inputs> = async (data) => {
      const errorList = getErrorArray(data.password);

      if (errorList.length > 0) {
        setFormError(errorList);
        return;
      }

      try {
        await onPasswordSubmit(data);
      } catch (error) {
        console.error(error);
      }
    };

    return (
      <section className={contentClassNames} style={style} data-testid="Form">
        <header className={styles.header}>
          <h1 className={styles.title}>Choose your new password</h1>
        </header>
        <FormComponent autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Row className={styles.row}>
            <Col span={24}>
              <Password
                label="Enter Password (At least 8 characters)"
                {...register('password', {
                  required: 'Required',
                })}
                isValid={!errors.password && !formError.length}
                showEndIconOnActive
                version="v2"
                placeholder="Choose your password"
              />
              {formError.length === 0 && !errors.password?.message && (
                <div className={styles.passwordRequirement}>
                  At least 8 characters, 1 uppercase character, 1 lowercase
                  character, 1 number or symbol
                </div>
              )}
              {errors.password?.message && (
                <div className={styles.errorMessage}>
                  {errors.password?.message}
                </div>
              )}
              {formError &&
                formError.map((error, index) => (
                  <div className={styles.errorMessage} key={index}>
                    {error.type === 'password' && (
                      <Icon
                        iconName="close"
                        size="small"
                        className={styles.icon}
                      />
                    )}
                    {error.message}
                  </div>
                ))}
            </Col>
          </Row>
          <Button
            type="submit"
            className={styles.submitButton}
            isFullWidth
            isLoading={isSubmitting}
          >
            Continue
          </Button>
        </FormComponent>
        <p className={styles.signinLink}>
          Return to{' '}
          <Link
            href={
              isRecruiter
                ? PAGE_ROUTES.SIGN_IN_RECRUITER
                : PAGE_ROUTES.SIGN_IN_SOCIETY
            }
            isPrimary
          >
            Sign in
          </Link>
        </p>
        <p className={styles.support}>
          Need more help? Ask at{' '}
          <Link href="mailto:hello@huzzle.app" isUnderlined>
            hello@huzzle.app
          </Link>
        </p>
      </section>
    );
  }
);

Form.displayName = 'From';
